import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../reducers';
import type { FakeAgentType } from './aiAgentsConsts';
import { AiChatMessageMode } from '@server/ai';
import type { ArtifactData } from '../common/ai-chat/aiChatTypes';
import { deleteFolder } from './agentsChatsReducer';

//This reducer holds only UI related state

export interface PendingArtifactToOpen {
  artifactId: string;
  versionId: string;
  artifactData: ArtifactData;
  targetChatId: string;
}

export interface AgentsHomeState {
  isExpanded: boolean;
  isCreateAgentActive: boolean;
  activeFakeAgentType?: FakeAgentType; //fake agents don't have ids
  advancedMode: AiChatMessageMode; // Session-scoped mode (saved to localStorage)
  selectedAdditionalOrgIds: string[]; // Multi-org mode: selected additional org IDs
  enabledMcpConnectionIds: string[]; // Connector toggles: IDs of connections enabled for the current chat session
  expandedFolderIds: string[]; // Folder IDs the user has explicitly opened; absence = collapsed
  isArtifactsViewActive: boolean; // Artifacts page view for an agent
  pendingArtifactToOpen?: PendingArtifactToOpen; // Artifact to open after navigation
  pendingInputText?: string; // Text to pre-fill in the chat input after navigation
  isIssuesPanelOpen: boolean; // Issues panel open state for monitoring agents
  selectedReplyText: string | null; // Text selected for "reply to" context (from issues panel or chat messages)
}

export const agentsHomeInitialState: AgentsHomeState = {
  isExpanded: true,
  isCreateAgentActive: false,
  activeFakeAgentType: undefined,
  advancedMode: AiChatMessageMode.None,
  selectedAdditionalOrgIds: [],
  enabledMcpConnectionIds: [],
  expandedFolderIds: [],
  isArtifactsViewActive: false,
  pendingArtifactToOpen: undefined,
  pendingInputText: undefined,
  isIssuesPanelOpen: false,
  selectedReplyText: null,
};

export const agentsHomeSlice = createSlice({
  name: 'agentsHome',
  initialState: agentsHomeInitialState,
  reducers: {
    setExpandMode: (state, action: PayloadAction<{ isExpanded: boolean }>) => {
      const { isExpanded } = action.payload;
      state.isExpanded = isExpanded;
    },
    toggleExpandMode: (state) => {
      state.isExpanded = !state.isExpanded;
    },
    setIsCreateActive: (state, action: PayloadAction<{ isActive: boolean }>) => {
      const { isActive } = action.payload;
      state.isCreateAgentActive = isActive;
    },
    resetAgentsHomeState: () => agentsHomeInitialState,
    setActiveFakeAgent: (state, action: PayloadAction<{ agentType: FakeAgentType }>) => {
      const { agentType } = action.payload;
      state.activeFakeAgentType = agentType;
    },
    resetActiveFakeAgent: (state) => {
      state.activeFakeAgentType = undefined;
    },
    setArtifactsViewActive: (state, action: PayloadAction<boolean>) => {
      state.isArtifactsViewActive = action.payload;
    },
    setPendingArtifactToOpen: (state, action: PayloadAction<PendingArtifactToOpen | undefined>) => {
      state.pendingArtifactToOpen = action.payload;
    },
    clearPendingArtifactToOpen: (state) => {
      state.pendingArtifactToOpen = undefined;
    },
    setIssuesPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isIssuesPanelOpen = action.payload;
      if (!action.payload) {
        state.selectedReplyText = null;
      }
    },
    setSelectedReplyText: (state, action: PayloadAction<string | null>) => {
      state.selectedReplyText = action.payload;
    },
    setAdvancedMode: (state, action: PayloadAction<{ advancedMode: AiChatMessageMode }>) => {
      state.advancedMode = action.payload.advancedMode;
      // Clear selected orgs when disabling multi-org mode
      if (action.payload.advancedMode !== AiChatMessageMode.MultiOrg) {
        state.selectedAdditionalOrgIds = [];
      }
    },
    setSelectedAdditionalOrgIds: (state, action: PayloadAction<{ orgIds: string[] }>) => {
      state.selectedAdditionalOrgIds = action.payload.orgIds;
    },
    clearSelectedAdditionalOrgIds: (state) => {
      state.selectedAdditionalOrgIds = [];
    },
    setEnabledMcpConnectionIds: (state, action: PayloadAction<string[]>) => {
      state.enabledMcpConnectionIds = action.payload;
    },
    setPendingInputText: (state, action: PayloadAction<string | undefined>) => {
      state.pendingInputText = action.payload;
    },
    clearPendingInputText: (state) => {
      state.pendingInputText = undefined;
    },
    toggleFolderExpanded: (state, action: PayloadAction<{ folderId: string }>) => {
      const { folderId } = action.payload;
      const idx = state.expandedFolderIds.indexOf(folderId);
      if (idx === -1) {
        state.expandedFolderIds.push(folderId);
      } else {
        state.expandedFolderIds.splice(idx, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(deleteFolder, (state, action) => {
      state.expandedFolderIds = state.expandedFolderIds.filter(
        (id) => id !== action.payload.folderId,
      );
    });
  },
});

export const {
  toggleExpandMode,
  setIsCreateActive,
  resetAgentsHomeState,
  setExpandMode,
  setActiveFakeAgent,
  resetActiveFakeAgent,
  setArtifactsViewActive,
  setAdvancedMode,
  setSelectedAdditionalOrgIds,
  clearSelectedAdditionalOrgIds,
  setEnabledMcpConnectionIds,
  setPendingArtifactToOpen,
  clearPendingArtifactToOpen,
  setPendingInputText,
  clearPendingInputText,
  setIssuesPanelOpen,
  setSelectedReplyText,
  toggleFolderExpanded,
} = agentsHomeSlice.actions;

export const selectIsExpandedAiChats = (state: RootState) => state.agentsHome.isExpanded;
export const selectIsCreateActive = (state: RootState) => state.agentsHome.isCreateAgentActive;
export const selectActiveFakeAgentType = (state: RootState) => {
  return state.agentsHome.activeFakeAgentType;
};
export const selectAdvancedMode = (state: RootState) => {
  return state.agentsHome.advancedMode;
};

export const selectSelectedAdditionalOrgIds = (state: RootState) => {
  return state.agentsHome.selectedAdditionalOrgIds;
};

export const selectEnabledMcpConnectionIds = (state: RootState) => {
  return state.agentsHome.enabledMcpConnectionIds;
};

export const selectIsArtifactsViewActive = (state: RootState) => {
  return state.agentsHome.isArtifactsViewActive;
};

export const selectPendingArtifactToOpen = (state: RootState) => {
  return state.agentsHome.pendingArtifactToOpen;
};

export const selectPendingInputText = (state: RootState) => {
  return state.agentsHome.pendingInputText;
};

export const selectIsIssuesPanelOpen = (state: RootState) => {
  return state.agentsHome.isIssuesPanelOpen;
};

export const selectSelectedReplyText = (state: RootState) => {
  return state.agentsHome.selectedReplyText;
};

export const selectIsFolderExpanded = (folderId: string) => (state: RootState) =>
  state.agentsHome.expandedFolderIds.includes(folderId);

export default agentsHomeSlice.reducer;
